import React, { memo } from "react";

import { classNames } from "helpers";
import { ShowcaseBackdrop, ShowcaseStage } from "ui/components/Showcase";
import Logo from "ui/modules/public/components/Logo/Logo";

import { SERVICE_DOCUMENTS_STEP, SERVICE_INFO_STEP, SERVICE_INTRO_TEXTS } from "./consts";
import ServiceDocumentsScene from "./scenes/ServiceDocumentsScene";
import ServiceInfoScene from "./scenes/ServiceInfoScene";
import { ServiceIntroProps, ServiceSceneProps } from "./types";

/** Scena za korak čarobnjaka servisnog naloga. */
const STEP_SCENES: Record<number, React.FC<ServiceSceneProps>> = {
  [SERVICE_INFO_STEP]: ServiceInfoScene,
  [SERVICE_DOCUMENTS_STEP]: ServiceDocumentsScene,
};

/**
 * Lijevi panel servisnog naloga: za korak vlastita scena s karticama koje prikazuju upisane
 * podatke, a ispod objašnjenje koraka. Isti obrazac kao intro čarobnjaka vozila.
 */
const ServiceIntro: React.FC<ServiceIntroProps> = ({ formValues, stepCurrent, stepCount }) => {
  const stepKey = STEP_SCENES[stepCurrent] ? stepCurrent : SERVICE_INFO_STEP;
  const Scene = STEP_SCENES[stepKey];
  const text = SERVICE_INTRO_TEXTS[stepKey];

  return (
    <div className="relative flex flex-col w-full h-full bg-blue-700 overflow-hidden">
      <ShowcaseBackdrop />
      <Logo variant="light" animated className="absolute top-8 left-8 z-10" />

      <div className="relative flex flex-1 min-h-0 items-center justify-center select-none pointer-events-none" aria-hidden="true">
        {/* `key`: novi korak iznova montira scenu, pa kartice ponovno ulete. */}
        <ShowcaseStage key={stepKey}>
          <Scene formValues={formValues} />
        </ShowcaseStage>
      </div>

      <div className="relative px-8 pb-12 text-center">
        <div className="text-xs font-semibold uppercase tracking-widest text-blue-200">
          Korak {stepKey + 1} od {stepCount}
        </div>
        <div key={stepKey} className="motion-safe:animate-showcaseFadeIn">
          <div className="mt-2 text-3xl font-bold leading-tight text-white">{text.title}</div>
          {/* Visina dva retka: opisi su različite duljine, a scena iznad ne smije skakati. */}
          <p className="mt-3 mx-auto max-w-md min-h-[3rem] text-base text-blue-100">{text.description}</p>
        </div>
        <div className="flex items-center justify-center gap-2 mt-6">
          {Array.from({ length: stepCount }, (_, index) => (
            <span
              key={index}
              className={classNames(
                "h-1.5 rounded-full transition-all duration-300",
                index === stepKey ? "w-6 bg-amber-400" : "w-1.5 bg-white/40"
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(ServiceIntro);
